/**
 * _app layout — authenticated shell (Sidebar + Island stage).
 *
 * Stage 3: wraps children in `AuthGate`. While Clerk loads or the
 * user is unauthenticated / not-yet-in-an-org, AuthGate renders a
 * spinner (and triggers a navigate to `/sign-in` or `/onboarding`).
 *
 * Each `/app/*` child route renders a single <Island> as its
 * content; <Shell> wraps it with the sidebar + paper-alt stage.
 *
 * Stage 6 additions (command palette):
 *   - <CommandPalette />          global ⌘K palette
 *   - <GlobalShortcuts />         registers every app-level keybinding
 *   - <GlobalCreateKeyDialog />   palette-openable API key dialog
 *   - <GlobalInviteMemberDialog /> palette-openable invite dialog
 *   - <ShortcutsDialog />         keyboard cheatsheet (`?`)
 *
 * Stage 7 additions (polish):
 *   - <RouteFocusManager />       focuses `#island-main` on every
 *                                 route change so Tab-order and
 *                                 screen readers track navigation.
 *
 * All six are mounted INSIDE `<AuthGate>` so they have a live Clerk
 * session + active org — no point trying to open "Invite member"
 * while the app is bootstrapping.
 */

import { UpsellDialog } from '@/components/primitives/UpsellDialog';
import { AuthGate } from '@/components/shell/AuthGate';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { GlobalCreateKeyDialog } from '@/components/shell/GlobalCreateKeyDialog';
import { GlobalInviteMemberDialog } from '@/components/shell/GlobalInviteMemberDialog';
import { GlobalShortcuts } from '@/components/shell/GlobalShortcuts';
import { RouteFocusManager } from '@/components/shell/RouteFocusManager';
import { Shell } from '@/components/shell/Shell';
import { ShortcutsDialog } from '@/components/shell/ShortcutsDialog';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app')({
  component: AppLayout,
});

function AppLayout(): ReactElement {
  return (
    <AuthGate>
      <Shell>
        <Outlet />
      </Shell>
      {/* Global 402 upsell — subscribes to the billingPrompt store
          wired in Stage 4. */}
      <UpsellDialog />
      {/* Stage 6 — command palette + shortcut registry + global dialogs. */}
      <GlobalShortcuts />
      <CommandPalette />
      <GlobalCreateKeyDialog />
      <GlobalInviteMemberDialog />
      <ShortcutsDialog />
      {/* Stage 7 — focus follows route changes into the island body. */}
      <RouteFocusManager />
    </AuthGate>
  );
}
